import { Toaster, ToasterMode } from '@creditsights/styleguide-web';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

interface ToastContextValue {
  show: (content: React.ReactNode, mode?: ToasterMode) => void;
  error: (content: React.ReactNode) => void;
  success: (content: React.ReactNode) => void;
}

export const ToastContext = createContext<ToastContextValue>({
  show: () => {},
  success: () => {},
  error: () => {},
});

let timer: NodeJS.Timeout | undefined;

export const ToastProvider = ({
  children,
  __testCloseTimer,
}: {
  children: React.ReactNode;
  __testCloseTimer?: number;
}): React.ReactElement => {
  const [content, setContent] = useState<React.ReactNode>();
  const [mode, setMode] = useState<ToasterMode>();

  const hide = useCallback(() => {
    setContent(undefined);
    setMode(undefined);
  }, []);

  const show = useCallback<ToastContextValue['show']>((content, mode) => {
    setContent(content);
    setMode(mode);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      hide,
      error: (content: React.ReactNode) => show(content, ToasterMode.ERROR),
      success: (content: React.ReactNode) => show(content, ToasterMode.SUCCESS),
    }),
    [show, hide]
  );

  useEffect(() => {
    clearTimeout(timer);

    if (content) {
      timer = setTimeout(() => {
        hide();
      }, __testCloseTimer ?? 5000);
    }
  }, [content, hide]);

  return (
    <>
      {content ? <Toaster textContent={content as JSX.Element} mode={mode} onClose={hide} /> : null}
      <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
    </>
  );
};
